import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { BackendService } from './backend.service';
import { CatalogAgent } from '../models/agent-catalog.model';

@Injectable({ providedIn: 'root' })
export class AgentCatalogService {
  private readonly _commandSelected$ = new Subject<string>();
  readonly commandSelected$ = this._commandSelected$.asObservable();

  private readonly _openCatalog$ = new Subject<void>();
  readonly openCatalog$ = this._openCatalog$.asObservable();

  /** Shared catalog fetch. Both the layout shell (command palette) and the chat
   *  component (slash menu) call getCatalog() at boot; without sharing they
   *  issue two identical GETs per page load. shareReplay(1) makes the second
   *  (and any later) subscriber join the in-flight request / replay the cached
   *  result instead. */
  private _catalog$: Observable<CatalogAgent[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly backend: BackendService,
  ) {}

  getCatalog(): Observable<CatalogAgent[]> {
    this._catalog$ ??= this._fetchCatalog().pipe(
      // An empty catalog is either a genuinely empty registry or the
      // catchError fallback — don't pin it; let the next caller refetch.
      tap(agents => { if (!agents.length) this._catalog$ = null; }),
      shareReplay(1),
    );
    return this._catalog$;
  }

  /** Drop the cached catalog and refetch — for callers that need fresh data
   *  (e.g. after a registry change) rather than the boot-time snapshot. */
  refreshCatalog(): Observable<CatalogAgent[]> {
    this._catalog$ = null;
    return this.getCatalog();
  }

  private _fetchCatalog(): Observable<CatalogAgent[]> {
    const url = this.backend.createAgentsUrl('agenthome/registry/catalog');
    return this.http
      .get<{ success: boolean; message: string; body: { agents: CatalogAgent[] } | null }>(url)
      .pipe(
        map(res => res?.body?.agents ?? []),
        catchError(() => of([])),
      );
  }

  selectCommand(command: string): void {
    this._commandSelected$.next(command);
  }

  triggerOpen(): void {
    this._openCatalog$.next();
  }
}
